import * as Utilities from './utilities';
import { PdfMakeContentRow } from '../entities/pdfmake-contentrow';

//AI generated function driven from unit test
//will take HTML content and return as a string array format
//useful for PDF make or any services that do not take HTML formatting
//html tags need to be separated into their own array item to be processed into the PDF.
export const pdfLineBreakRow = {
  text: '\n',
  lineHeight: 0.5, // default is ~1.2–1.5, smaller value reduces gap
  fontSize: 1,
} as PdfMakeContentRow;
export const pdfLinkColor = '#4EADEA';

//checks if row item has any data. if text has no valid value, then return false
//allows value ' ' because that indicates spacing
function pdfMakeRowHasData(row: PdfMakeContentRow) {
  if (typeof row.text === 'string' && !Utilities.hasValue(row.text)) {
    return false;
  } else if (Array.isArray(row.text) && row.text.length === 0) {
    return false;
  }
  return true;
}

///Collapse whitespace cleanup
function pdfMakeNormalizeText(txt: string): string {
  return (txt ?? '').replace(/\s+/g, ' ');
}

///Returns and wraps tags returning them as PdfMakeContentRow
function pdfMakeExtractFormatting(el: HTMLElement): PdfMakeContentRow {
  const tag = el.tagName.toUpperCase();
  const format = {} as PdfMakeContentRow;

  if (['STRONG', 'B'].includes(tag) || el.classList.contains('bold')) {
    format.bold = true;
  }
  if (['EM', 'I'].includes(tag) || el.classList.contains('italics')) {
    format.italics = true;
  }
  if (tag === 'A') {
    format.link = el.getAttribute('href') || undefined;
    format.color = pdfLinkColor;
  }

  return format;
}

function pdfMakeCombineInlineItems(originalObj: PdfMakeContentRow[]) {
  const combined: PdfMakeContentRow[] = [];
  let buffer: (string | PdfMakeContentRow)[] = [];

  const flush = () => {
    if (buffer.length) {
      combined.push({ text: buffer });
      buffer = [];
    }
  };

  for (const item of originalObj) {
    if (item?.text === '\n') {
      flush();
      combined.push(pdfLineBreakRow);
      continue;
    }

    if (item?.orderedList || item?.unorderedList) {
      flush();
      // console.log(item);
      // if (Array.isArray(item.text) && item.text[0]) {
      //   item.text[0] = (item.text[0] as string).replace('&bull;', '• ');
      //   item.style = 'marginLeftBottom';
      //   item.margin = [20, 0, 0, 0];
      // }
      if (pdfMakeRowHasData(item)) {
        combined.push(item);
      }

      continue;
    }
    // console.log(combined);
    const inline = item.inline;
    if (!inline) {
      flush();
      if (pdfMakeRowHasData(item)) {
        combined.push(item);
      }
      continue;
    }

    // normalize to inline parts
    const parts = Array.isArray(item.text)
      ? item.text
      : typeof item.text === 'string'
      ? [item]
      : [item.text ?? String(item)];

    parts.forEach((p) => {
      if (typeof p === 'string') buffer.push(p);
      else if (p && typeof p === 'object') {
        const obj = {
          text: p.text,
          bold: p.bold,
          italics: p.italics,
          link: p.link,
          color: p.color,
        } as PdfMakeContentRow;
        if (pdfMakeRowHasData(obj)) {
          buffer.push(obj);
        }
      }
    });
  }

  flush();

  // Loop through combined array and group sequential list items
  const finalArray: PdfMakeContentRow[] = [];
  let listBuffer: PdfMakeContentRow[] = [];
  let currentType: 'ul' | 'ol' | null = null;

  const flushListBuffer = () => {
    if (!listBuffer.length || !currentType) return;

    const listItem: PdfMakeContentRow = { ul: [], ol: [] };
    listBuffer.forEach((item) => {
      if (item.unorderedList) {
        listItem.ul.push({ text: item.text, style: 'listItem' });
      } else if (item.orderedList) {
        listItem.ol.push({ text: item.text, style: 'listItem' });
      }
    });

    finalArray.push(listItem);
    listBuffer = [];
    currentType = null;
  };

  combined.forEach((item) => {
    if (item.unorderedList || item.orderedList) {
      const type = item.unorderedList ? 'ul' : 'ol';
      // Flush previous list if type changes
      if (currentType && currentType !== type) {
        flushListBuffer();
      }
      currentType = type;
      listBuffer.push(item);
    } else {
      flushListBuffer();
      finalArray.push(item);
    }
  });

  // Flush any remaining list items
  flushListBuffer();

  return finalArray;
}

//NOTE: do not nest tags like this <a><b><i>test</i></b></a>. Instead use assign classes <a class="bold italics">test</a>
export function formatHtmlToArray(htmlInput: string, removeAnchors = false): PdfMakeContentRow[] {
  if (!htmlInput) return [];

  // preprocess tags
  let inputString = htmlInput
    .replace(/<p>/g, '')
    .replace(/<br\s*\/?>/g, '<br>')
    .replace(/<\/p>/g, '<br>')
    .replace(/<br><br>/g, '<br>');

  // optional: strip all anchor tags completely
  if (removeAnchors) {
    // remove <a ...> and </a> while keeping the inner text
    inputString = inputString.replace(/<a\b[^>]*>/gi, '').replace(/<\/a>/gi, '');
  }

  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = inputString;

  const formattedArray: PdfMakeContentRow[] = [];

  const buildInlinePartsFromNode = (node: Node): (string | PdfMakeContentRow)[] => {
    const parts: (string | PdfMakeContentRow)[] = [];

    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const txt = pdfMakeNormalizeText(child.textContent ?? '');
        if (txt) parts.push(txt);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const format = pdfMakeExtractFormatting(el);

        if (Object.keys(format).length > 0) {
          parts.push({ text: el.textContent || '', ...format });
        } else {
          // recurse deeper if no direct formatting
          parts.push(...buildInlinePartsFromNode(el));
        }
      }
    });

    return parts;
  };

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = pdfMakeNormalizeText(node.textContent ?? '');
      if (txt) formattedArray.push({ text: txt, inline: true });
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toUpperCase();

    switch (tag) {
      case 'OL':
      case 'UL':
        el.querySelectorAll('li').forEach((li, index) => {
          const parts = buildInlinePartsFromNode(li);
          const row = {
            orderedList: tag === 'OL',
            unorderedList: tag === 'UL',
            // text: tag === 'OL' ? [`${index + 1}. `, ...parts] : ['• ', ...parts],
            text: [...parts],
            margin: [20, 0, 0, 0],
          } as PdfMakeContentRow;
          if (pdfMakeRowHasData(row)) {
            formattedArray.push(row);
          }
        });
        break;

      case 'BR':
        formattedArray.push(pdfLineBreakRow);
        break;

      case 'H1':
      case 'H2': {
        const row = {
          text: (el.textContent ?? '').trim(),
          style: tag === 'H1' ? 'h1' : 'h2',
          bold: true,
        } as PdfMakeContentRow;
        if (pdfMakeRowHasData(row)) {
          formattedArray.push(row);
        }
        break;
      }
      default: {
        const format = pdfMakeExtractFormatting(el);
        if (Object.keys(format).length > 0) {
          const row = { text: el.textContent ?? '', inline: true, ...format };
          if (pdfMakeRowHasData(row)) {
            formattedArray.push(row);
          }
        } else {
          el.childNodes.forEach((c) => processNode(c));
        }
      }
    }
  };

  tempContainer.childNodes.forEach((n) => processNode(n));
  const pdfMakeContent = formattedArray.length > 0 ? pdfMakeCombineInlineItems(formattedArray) : formattedArray;
  // console.log(pdfMakeContent);
  return pdfMakeContent;
}

//collection of display values converted into an pdfmake unordered list collection.
export function unorderedList(textList: string[], ulStyle = 'marginBottom', liStyle = '') {
  const list = {
    ul: [],
    style: ulStyle,
  };

  textList.forEach((text) => {
    list.ul.push({
      text: text,
      // style: 'listItemMargin',
      style: liStyle,
    });
  });
  return list;
}

//insert HTML content into PDF make (into existing content collection)
export function insertHtmlInPDF(contentArray: any[], labelContent: string, contentStyle = 'marginBottom') {
  const pdfContent = formatHtmlToArray(labelContent);
  pdfContent.forEach((contentRow) => {
    contentArray.push({
      text: contentRow,
      style: contentStyle,
    });
  });
}
